import { useLazyQuery, useMutation, useQuery } from "@vue/apollo-composable";
import type { ComputedRef, Ref } from "vue";

import {
  CHECK_ORGANIZATION_SHORT_NAME,
  CREATE_ORGANIZATION,
  DELETE_ORGANIZATION,
  GENERATE_ORGANIZATION_SHORT_NAMES,
  GET_ORGANIZATION_MEMBERS,
  GET_ORGANIZATION,
  GET_ORGANIZATIONS,
  RESTORE_ORGANIZATION,
  SWITCH_ORG_MUTATION,
  UPDATE_ORGANIZATION,
} from "./organization.gql";
import type {
  CheckOrganizationShortNameResult,
  CheckOrganizationShortNameVariables,
  CreateOrganizationResult,
  CreateOrganizationVariables,
  DeleteOrganizationResult,
  DeleteOrganizationVariables,
  RestoreOrganizationResult,
  RestoreOrganizationVariables,
  GenerateOrganizationShortNamesResult,
  GenerateOrganizationShortNamesVariables,
  GetOrganizationResult,
  GetOrganizationVariables,
  OrganizationMembersResult,
  OrganizationMembersVariables,
  OrganizationsQueryResult,
  OrganizationsQueryVariables,
  SwitchOrgMutationResult,
  SwitchOrgMutationVariables,
  UpdateOrganizationResult,
  UpdateOrganizationVariables,
} from "./organization.gql";

export const useOrganizationsQuery = (variables?: Ref<OrganizationsQueryVariables>) =>
  useQuery<OrganizationsQueryResult, OrganizationsQueryVariables>(
    GET_ORGANIZATIONS,
    variables!,
  );

export const useOrganizationQuery = (variables: Ref<GetOrganizationVariables>) =>
  useQuery<GetOrganizationResult, GetOrganizationVariables>(
    GET_ORGANIZATION,
    variables,
    () => ({ enabled: !!variables.value.organizationId }),
  );

export const useOrganizationLazyQuery = () =>
  useLazyQuery<GetOrganizationResult, GetOrganizationVariables>(
    GET_ORGANIZATION,
  );

export const useCreateOrganizationMutation = () =>
  useMutation<CreateOrganizationResult, CreateOrganizationVariables>(CREATE_ORGANIZATION);

export const useUpdateOrganizationMutation = () =>
  useMutation<UpdateOrganizationResult, UpdateOrganizationVariables>(UPDATE_ORGANIZATION);

export const useDeleteOrganizationMutation = () =>
  useMutation<DeleteOrganizationResult, DeleteOrganizationVariables>(
    DELETE_ORGANIZATION,
  );

export const useRestoreOrganizationMutation = () =>
  useMutation<RestoreOrganizationResult, RestoreOrganizationVariables>(
    RESTORE_ORGANIZATION,
  );

export const useGenerateOrganizationShortNamesLazyQuery = () =>
  useLazyQuery<GenerateOrganizationShortNamesResult, GenerateOrganizationShortNamesVariables>(
    GENERATE_ORGANIZATION_SHORT_NAMES,
    undefined,
    { fetchPolicy: "network-only" },
  );

export const useCheckOrganizationShortNameLazyQuery = () =>
  useLazyQuery<CheckOrganizationShortNameResult, CheckOrganizationShortNameVariables>(
    CHECK_ORGANIZATION_SHORT_NAME,
    undefined,
    { fetchPolicy: "network-only" },
  );

export const useSwitchOrgMutation = () =>
  useMutation<SwitchOrgMutationResult, SwitchOrgMutationVariables>(SWITCH_ORG_MUTATION);

export const useOrganizationMembersQuery = (
  variables: Ref<OrganizationMembersVariables>,
  enabled?: ComputedRef<boolean>,
) =>
  useQuery<OrganizationMembersResult, OrganizationMembersVariables>(
    GET_ORGANIZATION_MEMBERS,
    variables,
    () => ({ enabled: enabled?.value !== false }),
  );
